import S from 's-js';
import { IAjax } from 'lib/Ajax';
import { AssertExtends } from 'lib/assertExtends';
import { jsonizeSignals } from 'lib/jsonizeSignals';
import { RequestQueue } from 'lib/requestQueue';
import TZDate from 'lib/TZDate';

import { AddPortCallRequest } from 'data/models/AddPortCallRequest';
import { CoordinationCard } from 'data/models/CoordinationCard';
import { Domain } from 'data/models/Domain';
import { PortCall } from 'data/models/PortCall';
import { PreviewBatchDatesRequestData } from 'data/models/PreviewBatchDatesRequest';
import { PreviewBatchDatesResponseData } from 'data/models/PreviewBatchDatesResponse';
import { RecurringBatchRequest } from 'data/models/RecurringBatchRequest';

import { Entrypoints } from 'core/app/entrypoints';

import { PortCallScheduleHeader } from 'portcall/portcallschedule/header';

import {
    MetroMapFramework,
    MetroMapFrameworkParams,
    MetroMapMilestone,
    MetroMapMilestoneParams,
    MilestoneParams
} from 'portcall/metromapframework/model';

import { AddPortCallTabModel } from './model';

export interface AddPortCallReviewTabParams {
    PortCall : PortCall;
    Request : AddPortCallRequest;
    ScheduleHeader : PortCallScheduleHeader;
    OnReviewTab : () => boolean;
}

export type AddPortCallReviewTab = AssertExtends<ReturnType<typeof AddPortCallReviewTab>, AddPortCallTabModel>;
export function AddPortCallReviewTab(
    ajax : IAjax,
    entrypoints : Entrypoints,
    domain : Domain,
    metroMapFrameworkModel : <TMilestone>(params : MetroMapFrameworkParams<TMilestone>) => MetroMapFramework<TMilestone>,
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    {
        PortCall,
        Request,
        ScheduleHeader,
        OnReviewTab
    } : AddPortCallReviewTabParams
) {
    const
        ccardModel = CoordinationCardViewModel,
        milestoneModel = (params : MilestoneParams) => ReviewTabMilestone(
            metroMapMilestoneModel,
            ccardModel,
            params
        ),
        Framework = metroMapFrameworkModel({
            PortCall,
            MilestoneConstructor: milestoneModel
        });

    const BatchRequest = S(() => Request.BatchRequest() && BatchRequestViewModel(
        ajax,
        entrypoints,
        domain,
        Request.BatchRequest()!,
        PortCall,
        OnReviewTab
    ));

    const UpdatingPreview = () =>
        !!BatchRequest()?.UpdatingPreview();

    return {
        ScheduleHeader,
        Framework,
        BatchRequest,
        UpdatingPreview,
        CanProceed: () => true
    };
}

export type ReviewTabMilestone = ReturnType<typeof ReviewTabMilestone>;
function ReviewTabMilestone(
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    ccardModel : (ccard : CoordinationCard) => CoordinationCardViewModel,
    params : MilestoneParams
) {
    const
        { Date } = params,
        metroMapMilestone = metroMapMilestoneModel(params),
        CoordinationCards = Date.CoordinationCards
            .map(ccardModel)
            .sort((ccard1, ccard2) => CoordinationCard.DisplayOrder(ccard1.Model, ccard2.Model));

    return {
        ...metroMapMilestone,
        CoordinationCards
    };
}

export type CoordinationCardViewModel = ReturnType<typeof CoordinationCardViewModel>;
function CoordinationCardViewModel(ccard : CoordinationCard) {
    return { Model: ccard };
}

export type BatchRequestViewModel = ReturnType<typeof BatchRequestViewModel>;
function BatchRequestViewModel(
    ajax : IAjax,
    entrypoints : Entrypoints,
    domain : Domain,
    batchReq : RecurringBatchRequest,
    portcall : PortCall,
    onReviewTab : () => boolean
) {
    const
        StartDate = S(() => portcall.Itinerary().find(pt => pt.IsPortArrival())?.Arrival.Estimated() ?? null),
        // We'll delegate to the server to figure out the overall date range for the requested batch. Of
        // course, we COULD do this date logic client-side, but the server already has responsibility for
        // determining portcall start dates on final submission, and this way we keep all that date logic
        // server-side and minimize the risk that we determine one set of start dates during preview and a
        // DIFFERENT set of start dates on final submission.
        req = S<PreviewBatchDatesRequestData | null>(() => onReviewTab() && StartDate() ? {
            DomainId: domain.Id(),
            StartDate: StartDate()!.toJSON(),
            BatchRequest: JSON.parse(jsonizeSignals(batchReq, [_ => batchReq.Batch]))
        } : null),
        resp = S.value<PreviewBatchDatesResponseData | null>(null),
        client = ajax.Url<PreviewBatchDatesRequestData, PreviewBatchDatesResponseData>(entrypoints.PreviewBatchDates),
        UpdatingPreview = S.value(false),
        reqQueue = new RequestQueue(async () => {
            if (req()) {
                UpdatingPreview(true);
                try {
                    resp(await client.Post(req()!));
                } finally {
                    UpdatingPreview(false);
                }
            }
        }),
        Preview = S(() => {
            if (!resp()) return null;

            const dates = resp()!.PortCallStartDates.map(d => new TZDate(d));

            let minDate : TZDate | null = null;
            for (let date of dates) {
                if (minDate === null || +minDate > +date) minDate = date;
            }

            let maxDate : TZDate | null = null;
            for (let date of dates) {
                if (maxDate === null || +maxDate < +date) maxDate = date;
            }

            return {
                BatchSize: dates.length,
                FirstPortCallArrival: minDate,
                LastPortCallArrival: maxDate
            };
        });

    S(() => req() && reqQueue.enqueueRequest());

    return {
        Model: batchReq,
        StartDate,
        UpdatingPreview,
        Preview
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/review.ts